import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  DollarSign,
  Check,
  X,
  Clock,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Users,
  Hammer,
  Home,
  Shield,
  Landmark,
  Receipt,
  TrendingUp
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Format currency
const formatCurrency = (value) => {
  if (!value && value !== 0) return "$0";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

// Payment type labels
const PAYMENT_TYPES = {
  earnest_money: "Earnest Money",
  assignment_fee: "Assignment Fee",
  contractor_draw: "Contractor Draw",
  closing_funds: "Closing Funds",
  realtor_commission: "Realtor Commission",
  lender_fee: "Lender Fee",
  title_fee: "Title Fee",
  refund: "Refund"
};

// Status badge component
function StatusBadge({ status }) {
  const config = {
    pending: { color: "text-amber-400", bg: "bg-amber-400/10", label: "Pending" },
    pending_approval: { color: "text-amber-400", bg: "bg-amber-400/10", label: "Awaiting Approval" },
    completed: { color: "text-emerald-400", bg: "bg-emerald-400/10", label: "Completed" },
    processing: { color: "text-blue-400", bg: "bg-blue-400/10", label: "Processing" },
    rejected: { color: "text-red-400", bg: "bg-red-400/10", label: "Rejected" },
    failed: { color: "text-red-400", bg: "bg-red-400/10", label: "Failed" }
  };

  const { color, bg, label } = config[status] || config.pending;

  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${color} ${bg}`}>
      {label}
    </span>
  );
}

// Transaction item component
function TransactionItem({ txn, onApprove, onReject }) {
  const isDeposit = txn.type === "deposit";
  const isPending = txn.status === "pending_approval";

  return (
    <div className="flex items-center justify-between py-3 border-b border-white/[0.06] last:border-0">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isDeposit ? "bg-emerald-500/10" : "bg-amber-500/10"
        }`}>
          {isDeposit ? (
            <ArrowDownToLine className="w-4 h-4 text-emerald-400" />
          ) : (
            <ArrowUpFromLine className="w-4 h-4 text-amber-400" />
          )}
        </div>
        <div>
          <p className="text-white text-sm font-medium">
            {PAYMENT_TYPES[txn.payment_type] || txn.payment_type}
          </p>
          <p className="text-white/40 text-xs">
            {txn.recipient_name || txn.description || new Date(txn.created_at).toLocaleDateString()}
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        <div className="text-right">
          <p className={`font-mono font-bold ${isDeposit ? "text-emerald-400" : "text-white"}`}>
            {isDeposit ? "+" : "-"}{formatCurrency(txn.amount)}
          </p>
          <StatusBadge status={txn.status} />
        </div>
        
        {isPending && onApprove && (
          <div className="flex gap-1 ml-2">
            <button
              onClick={() => onApprove(txn.transaction_id)}
              className="p-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 rounded text-emerald-400 transition-colors"
              title="Approve"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={() => onReject(txn.transaction_id)}
              className="p-1.5 bg-red-500/10 hover:bg-red-500/20 rounded text-red-400 transition-colors"
              title="Reject"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Deposit Modal
function DepositModal({ dealId, onClose, onSuccess }) {
  const [amount, setAmount] = useState("");
  const [paymentType, setPaymentType] = useState("earnest_money");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/deal-wallet/deposit`,
        {
          deal_id: dealId,
          amount: parseFloat(amount),
          payment_type: paymentType,
          description: description || undefined,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );

      // Redirect to Stripe checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create deposit");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Deposit to Deal Wallet</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-white/70 text-sm mb-1.5 block">Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">$</span>
              <Input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-black border-white/[0.08] text-white pl-8"
                placeholder="10,000.00"
              />
            </div>
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1.5 block">Payment Type</label>
            <select
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
              className="w-full bg-black border border-white/[0.08] rounded-md text-white p-2.5 text-sm"
            >
              <option value="earnest_money">Earnest Money Deposit</option>
              <option value="assignment_fee">Assignment Fee</option>
              <option value="closing_funds">Closing Funds</option>
              <option value="contractor_draw">Contractor Draw Funding</option>
            </select>
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1.5 block">Description (Optional)</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-black border-white/[0.08] text-white"
              placeholder="Initial deposit for property acquisition"
            />
          </div>

          {amount && parseFloat(amount) > 0 && (() => {
            const amt = parseFloat(amount);
            // EMD: flat $20, others: 1.5% capped at $500
            const fee = paymentType === 'earnest_money' ? 20 : Math.min(amt * 0.015, 500);
            const feeLabel = paymentType === 'earnest_money' ? 'Platform Fee (EMD: $20 flat)' : 'Platform Fee (1.5% max $500)';
            return (
              <div className="p-3 bg-[#D4AF37]/[0.05] border border-[#D4AF37]/20 rounded">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/60">Deposit Amount</span>
                  <span className="text-white">{formatCurrency(amt)}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-white/60">{feeLabel}</span>
                  <span className="text-white/70">{formatCurrency(fee)}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-white/[0.06]">
                  <span className="text-white font-medium">Total Charge</span>
                  <span className="text-[#D4AF37] font-bold">{formatCurrency(amt + fee)}</span>
                </div>
              </div>
            );
          })()}
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/[0.08] text-white">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !amount}
            className="flex-1 bg-[#D4AF37] hover:bg-[#C4A030] text-black font-semibold"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue to Payment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Payout Modal
function PayoutModal({ dealId, escrowBalance, onClose, onSuccess }) {
  const [recipientType, setRecipientType] = useState("contractor");
  const [recipientId, setRecipientId] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentType, setPaymentType] = useState("contractor_draw");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);

  useEffect(() => {
    fetchRecipients();
  }, [recipientType]);

  const fetchRecipients = async () => {
    setLoadingRecipients(true);
    try {
      const endpoint = {
        contractor: "/api/contractors/all",
        realtor: "/api/realtor/all",
        lender: "/api/lenders/all",
        title: "/api/title/all"
      }[recipientType];

      const res = await axios.get(`${BACKEND_URL}${endpoint}`, { withCredentials: true });
      setRecipients(res.data || []);
    } catch (err) {
      // Fallback to empty
      setRecipients([]);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleSubmit = async () => {
    if (!recipientId || !amount || parseFloat(amount) <= 0) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (parseFloat(amount) > escrowBalance) {
      toast.error("Amount exceeds wallet balance");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/deal-wallet/payout`,
        {
          deal_id: dealId,
          recipient_type: recipientType,
          recipient_id: recipientId,
          amount: parseFloat(amount),
          payment_type: paymentType,
          description: description || undefined
        },
        { withCredentials: true }
      );

      toast.success(`Payout of ${formatCurrency(parseFloat(amount))} created`);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create payout");
    } finally {
      setLoading(false);
    }
  };

  const recipientIdField = {
    contractor: "contractor_id",
    realtor: "realtor_id",
    lender: "lender_id",
    title: "company_id"
  }[recipientType];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Create Payout</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4 p-3 bg-emerald-500/[0.05] border border-emerald-500/20 rounded">
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-sm">Available Balance</span>
            <span className="text-emerald-400 font-mono font-bold">{formatCurrency(escrowBalance)}</span>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-white/70 text-sm mb-1.5 block">Recipient Type</label>
            <select
              value={recipientType}
              onChange={(e) => {
                setRecipientType(e.target.value);
                setRecipientId("");
                setPaymentType({
                  contractor: "contractor_draw",
                  realtor: "realtor_commission",
                  lender: "lender_fee",
                  title: "title_fee"
                }[e.target.value]);
              }}
              className="w-full bg-black border border-white/[0.08] rounded-md text-white p-2.5 text-sm"
            >
              <option value="contractor">Contractor</option>
              <option value="realtor">Realtor</option>
              <option value="lender">Lender</option>
              <option value="title">Title Company</option>
            </select>
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1.5 block">Select Recipient</label>
            <select
              value={recipientId}
              onChange={(e) => setRecipientId(e.target.value)}
              className="w-full bg-black border border-white/[0.08] rounded-md text-white p-2.5 text-sm"
              disabled={loadingRecipients}
            >
              <option value="">Select {recipientType}...</option>
              {recipients.map((r) => (
                <option key={r[recipientIdField]} value={r[recipientIdField]}>
                  {r.company_name || r.name} ({r.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1.5 block">Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50">$</span>
              <Input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-black border-white/[0.08] text-white pl-8"
                placeholder="5,000.00"
              />
            </div>
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1.5 block">Description (Optional)</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="bg-black border-white/[0.08] text-white"
              placeholder="Draw #1 - Foundation complete"
            />
          </div>

          {amount && parseFloat(amount) > 0 && (() => {
            const amt = parseFloat(amount);
            const fee = Math.min(amt * 0.015, 500); // 1.5% capped at $500
            const net = amt - fee;
            return (
              <div className="p-3 bg-white/[0.02] border border-white/[0.06] rounded">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/60">Payout Amount</span>
                  <span className="text-white">{formatCurrency(amt)}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-white/60">Platform Fee (1.5% max $500)</span>
                  <span className="text-white/70">-{formatCurrency(fee)}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2 pt-2 border-t border-white/[0.06]">
                  <span className="text-white font-medium">Net to Recipient</span>
                  <span className="text-emerald-400 font-bold">{formatCurrency(net)}</span>
                </div>
              </div>
            );
          })()}
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={onClose} className="flex-1 border-white/[0.08] text-white">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !recipientId || !amount}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Payout"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Main Escrow Panel Component
export default function EscrowPanel({ dealId }) {
  const [escrow, setEscrow] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);

  useEffect(() => {
    fetchEscrowData();
    
    // Check for deposit success
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const depositSuccess = params.get("escrow_deposit");
    
    if (sessionId && depositSuccess === "success") {
      pollDepositStatus(sessionId);
      // Clean up URL
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [dealId]);

  const fetchEscrowData = async () => {
    try {
      const [escrowRes, txnRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/deal-wallet/${dealId}`, { withCredentials: true }).catch(() => null),
        axios.get(`${BACKEND_URL}/api/deal-wallet/${dealId}/transactions`, { withCredentials: true }).catch(() => ({ data: { transactions: [] } }))
      ]);

      if (escrowRes?.data) {
        setEscrow(escrowRes.data);
      }
      setTransactions(txnRes?.data?.transactions || []);
    } catch (err) {
      // Escrow might not exist yet
    } finally {
      setLoading(false);
    }
  };

  const pollDepositStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error("Payment verification timed out. Please check your dashboard.");
      return;
    }

    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/deal-wallet/deposit/status/${sessionId}`,
        { withCredentials: true }
      );

      if (res.data.status === "completed") {
        toast.success(`${formatCurrency(res.data.amount)} deposited to deal wallet!`);
        fetchEscrowData();
      } else if (res.data.payment_status === "unpaid") {
        setTimeout(() => pollDepositStatus(sessionId, attempts + 1), 2000);
      }
    } catch (err) {
      setTimeout(() => pollDepositStatus(sessionId, attempts + 1), 2000);
    }
  };

  const handleApprove = async (transactionId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/deal-wallet/payout/${transactionId}/approve`,
        {},
        { withCredentials: true }
      );
      toast.success("Payout approved");
      fetchEscrowData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve payout");
    }
  };

  const handleReject = async (transactionId) => {
    try {
      await axios.post(
        `${BACKEND_URL}/api/deal-wallet/payout/${transactionId}/reject`,
        { reason: "Rejected by operator" },
        { withCredentials: true }
      );
      toast.success("Payout rejected, funds returned to deal wallet");
      fetchEscrowData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject payout");
    }
  };

  if (loading) {
    return (
      <div className="bg-[#0A0A0A]/80 border border-white/[0.06] p-4 rounded-lg">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0A0A0A]/80 border border-white/[0.06] rounded-lg overflow-hidden">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center justify-center">
            <Wallet className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Deal Wallet</h3>
            <p className="text-white/40 text-xs">Funds held by assigned title company</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-[10px] text-white/40 uppercase">Balance</p>
            <p className="text-emerald-400 font-mono font-bold text-lg">
              {formatCurrency(escrow?.balance || 0)}
            </p>
          </div>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-white/40" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white/40" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-white/[0.06] p-4">
          {/* Title Company - Fund Authority */}
          {escrow?.title_company_name && (
            <div className="flex items-center gap-3 p-3 mb-4 bg-emerald-500/[0.05] border border-emerald-500/20 rounded-lg">
              <Shield className="w-5 h-5 text-emerald-400" />
              <div className="flex-1">
                <p className="text-xs text-emerald-400/70">Fund Authority</p>
                <p className="text-white font-medium">{escrow.title_company_name}</p>
              </div>
              {escrow.stripe_ready ? (
                <span className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded">
                  STRIPE CONNECTED
                </span>
              ) : (
                <span className="text-xs px-2 py-1 bg-amber-500/20 text-amber-400 rounded">
                  PENDING SETUP
                </span>
              )}
            </div>
          )}
          
          {/* Quick Stats */}
          {escrow && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="p-3 bg-emerald-500/[0.05] border border-emerald-500/20 rounded">
                <p className="text-[10px] text-white/40 uppercase">Total Deposits</p>
                <p className="text-emerald-400 font-mono">{formatCurrency(escrow.total_deposits || escrow.total_deposited || 0)}</p>
              </div>
              <div className="p-3 bg-amber-500/[0.05] border border-amber-500/20 rounded">
                <p className="text-[10px] text-white/40 uppercase">Disbursements</p>
                <p className="text-amber-400 font-mono">{formatCurrency(escrow.total_disbursements || escrow.total_withdrawn || 0)}</p>
              </div>
              <div className="p-3 bg-[#D4AF37]/[0.05] border border-[#D4AF37]/20 rounded">
                <p className="text-[10px] text-white/40 uppercase">Platform Fees</p>
                <p className="text-[#D4AF37] font-mono">{formatCurrency(escrow.platform_fees_collected || escrow.total_fees_collected || 0)}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 mb-4">
            <Button
              onClick={() => setShowDepositModal(true)}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              <ArrowDownToLine className="w-4 h-4 mr-2" />
              Deposit
            </Button>
            <Button
              onClick={() => setShowPayoutModal(true)}
              disabled={!escrow || escrow.balance <= 0}
              className="flex-1 bg-[#D4AF37] hover:bg-[#C4A030] text-black"
            >
              <ArrowUpFromLine className="w-4 h-4 mr-2" />
              Pay Out
            </Button>
          </div>

          {/* Recent Transactions */}
          <div>
            <h4 className="text-white/50 text-xs uppercase tracking-wider mb-2">Recent Transactions</h4>
            {transactions.length === 0 ? (
              <p className="text-white/30 text-sm text-center py-4">No transactions yet</p>
            ) : (
              <div className="space-y-0">
                {transactions.slice(0, 5).map((txn) => (
                  <TransactionItem
                    key={txn.transaction_id}
                    txn={txn}
                    onApprove={handleApprove}
                    onReject={handleReject}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {showDepositModal && (
        <DepositModal
          dealId={dealId}
          onClose={() => setShowDepositModal(false)}
          onSuccess={fetchEscrowData}
        />
      )}

      {showPayoutModal && escrow && (
        <PayoutModal
          dealId={dealId}
          escrowBalance={escrow.balance}
          onClose={() => setShowPayoutModal(false)}
          onSuccess={fetchEscrowData}
        />
      )}
    </div>
  );
}
